/**
 * Learning Page
 * Training and learning resources, certifications, and courses
 */

import React from 'react';
import PageLayout from '../components/PageLayout';
import styles from './LearningPage.module.css';

interface CourseCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: string;
  link: string;
}

const LearningPage: React.FC = () => {
  const courseCards: CourseCard[] = [
    {
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
        </svg>
      ),
      title: 'Courses & Learning Paths',
      description: 'From foundational to advanced topics (e.g., data protection, fundamentals, APSs)',
      action: 'View Courses',
      link: 'https://readiverse.commvault.com/learn'
    },
    {
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 10L12 5L2 10L12 15L22 10Z"></path>
          <path d="M6 12.5V16C6 16 8.5 19 12 19C15.5 19 18 16 18 16V12.5"></path>
          <line x1="22" y1="10" x2="22" y2="16"></line>
        </svg>
      ),
      title: 'Workshops & Instructor-Led',
      description: 'From Cyber Resilience Planning Workshops to Data Protection best practices',
      action: 'View Workshops',
      link: 'https://readiverse.commvault.com/learn'
    },
    {
      icon: (
        <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="6"></circle>
          <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"></path>
        </svg>
      ),
      title: 'Certifications & Badges',
      description: 'Recognitions of expertise: tied to Credly for badge issuance',
      action: 'Get Certified',
      link: 'https://readiverse.commvault.com/learn'
    }
  ];

  const handleActionClick = (link: string) => {
    window.open(link, '_blank');
  };

  return (
    <PageLayout>
      <div className={styles.unifiedContainer}>
        {/* Readiverse Info */}
        <section className={styles.readiverseSection}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.readiverseIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.5 2A5 5 0 0 1 12 10a5 5 0 0 1 2.5-8"></path>
                <path d="M12 10a5 5 0 0 1 0 10"></path>
                <path d="M12 2a5 5 0 0 0-2.5 8 5 5 0 0 0 0 10 5 5 0 0 0 5-5 5 5 0 0 0-2.5-5"></path>
                <path d="M12 10V2"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </span>
            Readiverse
          </h2>
          <p className={styles.subtitle}>A higher plane of knowledge and resilience</p>
          <ul className={styles.featureList}>
            <li>Readiverse is Commvault's cyber-resilience learning and certification platform.</li>
            <li>It's designed for customers, partners, and anyone interested in cyber readiness, not just technical specialists.</li>
            <li>The platform consolidates training, workshops, certifications, on-demand content, and community resources under one roof.</li>
          </ul>
          <div className={styles.smallLine}></div>
        </section>

        {/* Course Cards Grid */}
        <div className={styles.courseGrid}>
          {courseCards.map((card, index) => (
            <div key={index} className={styles.courseCard}>
              <h3>{card.title}</h3>
              <div className={styles.cardHeaderLine}></div>
              <div className={styles.cardIconWrapper}>
                <div className={styles.squareIcon}>
                  <div className={styles.iconOuter}></div>
                  <div className={styles.iconInner}>
                    <div className={styles.cardIcon}>{card.icon}</div>
                  </div>
                </div>
              </div>
              <p>{card.description}</p>
              <button 
                className={styles.cardButton}
                onClick={() => handleActionClick(card.link)}
              >
                {card.action}
              </button>
            </div>
          ))}
        </div>

        {/* Value & Benefits */}
        <section className={styles.benefitsSection}>
          <h3>Value & Benefits</h3>
          <ul className={styles.benefitsList}>
            <li><strong>Centralizes training:</strong> makes it easier for users across the organization to find and access relevant learning.</li>
            <li><strong>Improves cyber resilience:</strong> offers courses and certifications that directly align with Commvault's solution domain (data protection, recovery).</li>
            <li><strong>Recognition & incentives:</strong> digital badges and certifications help motivate adoption and reward learning progress.</li>
            <li><strong>Supports growth of user proficiency:</strong> helps customers and partners deepen their capabilities, leading to better outcomes with Commvault products.</li>
            <li><strong>Streamlines credential management:</strong> by integrating with existing accounts and migrating older content, it reduces friction.</li>
          </ul>
        </section>

        {/* Launch Button */}
        <div className={styles.launchSection}>
          <button 
            className={styles.launchButtonLarge}
            onClick={() => handleActionClick('https://readiverse.commvault.com/learn')}
          >
            Launch Readiverse
          </button>
        </div>
      </div>
    </PageLayout>
  );
};

export default LearningPage;
